import * as React from 'react';
import { useForm } from 'react-hook-form';
import { Loader } from 'lucide-react';
import { Form, FormField } from 'components/ui/form';
import { InputGs, SelectGs } from 'components/ui/Forms';
import { Button } from 'components/ui/ButtonGs';
import type { ViagensControleFilters } from '../types';
import { useViagensControleSearchParams } from 'hooks/store/viagens-controle';

type Ref = {
  resetFilters: () => void;
  getFormData: () => ViagensControleFilters;
};

type Props = {
  onSubmitFilters: (filters: ViagensControleFilters) => void;
  onResetFilters: () => void;
  filterLoadingAction?: 'filter' | 'reset' | null;
  showApprovalStatusFilter?: boolean;
};

type FormData = {
  idViagem: string;
  shipment: string;
  notas: string;
  conhecimento: string;
  viagem: string;
  placa: string;
  agendamentoInicio: string;
  agendamentoFim: string;
  approvalStatus: string;
};

const approvalStatusOptions = [
  { name: 'Todas', value: 'all' },
  { name: 'Pendente', value: 'pending' },
  { name: 'Revalidar', value: 'revalidar' },
  { name: 'Aprovada', value: 'approved' },
  { name: 'Reprovada', value: 'rejected' },
];

export type ViagensControleFilterRef = Ref;

export const ViagensControleFilter = React.forwardRef<Ref, Props>(
  (
    {
      onSubmitFilters,
      onResetFilters,
      filterLoadingAction,
      showApprovalStatusFilter = true,
    },
    ref
  ) => {
    const { filters } = useViagensControleSearchParams({ persisted: true });
    const form = useForm<FormData>({
      defaultValues: {
        idViagem: filters.idViagem ?? '',
        shipment: filters.shipment ?? '',
        notas: filters.notas ?? '',
        conhecimento: filters.conhecimento ?? '',
        viagem: filters.viagem ?? '',
        placa: filters.placa ?? '',
        agendamentoInicio: filters.agendamentoInicio ?? '',
        agendamentoFim: filters.agendamentoFim ?? '',
        approvalStatus: filters.approvalStatus ?? 'all',
      },
    });

    const buildFilters = React.useCallback((formData: FormData) => {
      return {
        idViagem: formData.idViagem?.trim() || undefined,
        shipment: formData.shipment?.trim() || undefined,
        notas: formData.notas?.trim() || undefined,
        conhecimento: formData.conhecimento?.trim() || undefined,
        viagem: formData.viagem?.trim() || undefined,
        placa: formData.placa?.trim() || undefined,
        agendamentoInicio: formData.agendamentoInicio || undefined,
        agendamentoFim: formData.agendamentoFim || undefined,
        approvalStatus:
          formData.approvalStatus === 'all'
            ? undefined
            : (formData.approvalStatus as ViagensControleFilters['approvalStatus']),
      };
    }, []);

    const resetFilters = React.useCallback(() => {
      form.reset(
        {
          idViagem: '',
          shipment: '',
          notas: '',
          conhecimento: '',
          viagem: '',
          placa: '',
          agendamentoInicio: '',
          agendamentoFim: '',
          approvalStatus: 'all',
        },
        { keepValues: false }
      );
      onResetFilters();
    }, [form, onResetFilters]);

    const getFormData = React.useCallback(() => {
      return buildFilters(form.getValues());
    }, [buildFilters, form]);

    const onFormSubmit = React.useCallback(
      (formData: FormData) => {
        onSubmitFilters(buildFilters(formData));
      },
      [buildFilters, onSubmitFilters]
    );

    React.useEffect(() => {
      form.reset({
        idViagem: filters.idViagem ?? '',
        shipment: filters.shipment ?? '',
        notas: filters.notas ?? '',
        conhecimento: filters.conhecimento ?? '',
        viagem: filters.viagem ?? '',
        placa: filters.placa ?? '',
        agendamentoInicio: filters.agendamentoInicio ?? '',
        agendamentoFim: filters.agendamentoFim ?? '',
        approvalStatus: filters.approvalStatus ?? 'all',
      });
    }, [filters, form]);

    React.useImperativeHandle(ref, () => ({
      resetFilters,
      getFormData,
    }));

    return (
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onFormSubmit)} className="w-full">
          <div
            className={`grid grid-cols-1 md:grid-cols-2 ${
              showApprovalStatusFilter ? 'xl:grid-cols-5' : 'xl:grid-cols-4'
            } gap-4`}
          >
            <FormField
              control={form.control}
              name="idViagem"
              render={({ field }) => (
                <InputGs {...field} type="text" label="ID viagem" />
              )}
            />
            <FormField
              control={form.control}
              name="placa"
              render={({ field }) => (
                <InputGs {...field} type="text" label="Placa" />
              )}
            />
            <FormField
              control={form.control}
              name="shipment"
              render={({ field }) => (
                <InputGs {...field} type="text" label="Shipment" />
              )}
            />
            <FormField
              control={form.control}
              name="notas"
              render={({ field }) => (
                <InputGs {...field} type="text" label="Nota" />
              )}
            />
            <FormField
              control={form.control}
              name="conhecimento"
              render={({ field }) => (
                <InputGs {...field} type="text" label="Conhecimento" />
              )}
            />
            <FormField
              control={form.control}
              name="viagem"
              render={({ field }) => (
                <InputGs {...field} type="text" label="Viagem" />
              )}
            />
            <FormField
              control={form.control}
              name="agendamentoInicio"
              render={({ field }) => (
                <InputGs {...field} type="date" label="Agendamento de" />
              )}
            />
            <FormField
              control={form.control}
              name="agendamentoFim"
              render={({ field }) => (
                <InputGs {...field} type="date" label="Agendamento até" />
              )}
            />
            {showApprovalStatusFilter && (
              <FormField
                control={form.control}
                name="approvalStatus"
                render={({ field }) => (
                  <SelectGs
                    {...field}
                    onValueChange={field.onChange}
                    label="Aprovação"
                    content={approvalStatusOptions}
                  />
                )}
              />
            )}
          </div>

          <div className="flex items-center mt-6 gap-4">
            <Button.Root
              type="button"
              size="sm"
              variant="secondary"
              disabled={Boolean(filterLoadingAction)}
              onClick={resetFilters}
            >
              <span className="relative grid place-items-center">
                <span
                  className={filterLoadingAction === 'reset' ? 'invisible' : ''}
                >
                  Limpar filtros
                </span>
                {filterLoadingAction === 'reset' && (
                  <Loader className="absolute h-4 w-4 animate-spin" />
                )}
              </span>
            </Button.Root>

            <Button.Root
              type="submit"
              size="sm"
              variant="primary"
              disabled={Boolean(filterLoadingAction)}
            >
              <span className="relative grid place-items-center">
                <span
                  className={
                    filterLoadingAction === 'filter' ? 'invisible' : ''
                  }
                >
                  Filtrar
                </span>
                {filterLoadingAction === 'filter' && (
                  <Loader className="absolute h-4 w-4 animate-spin" />
                )}
              </span>
            </Button.Root>
          </div>
        </form>
      </Form>
    );
  }
);

ViagensControleFilter.displayName = 'ViagensControleFilter';
